import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, Tenant, UserRole, OnlineStoreState } from '../types'

export interface StoredSubscription {
  status: string
  current_period_end: string
}

/** Why an authenticated user has no active tenant membership. */
export type OrphanReason = 'no_workspace' | 'deactivated'

interface AuthState {
  user: User | null
  tenant: Tenant | null
  role: UserRole | null
  erpApiKey: string | null
  erpApiSecret: string | null
  isAuthenticated: boolean
  isInitializing: boolean
  isReadOnly: boolean
  subscription: StoredSubscription | null
  onlineStore: OnlineStoreState | null
  forcePasswordChange: boolean
  isTenantResolved: boolean
  orphanReason: OrphanReason | null

  setUser: (user: User | null) => void
  setTenant: (tenant: Tenant | null) => void
  setRole: (role: UserRole | null) => void
  setErpCredentials: (apiKey: string, apiSecret: string) => void
  setInitializing: (v: boolean) => void
  setIsReadOnly: (v: boolean) => void
  setSubscription: (sub: StoredSubscription | null) => void
  setOnlineStore: (s: OnlineStoreState | null) => void
  setForcePasswordChange: (v: boolean) => void
  setTenantResolved: (v: boolean) => void
  setOrphanReason: (r: OrphanReason | null) => void
  clearSession: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tenant: null,
      role: null,
      erpApiKey: null,
      erpApiSecret: null,
      isAuthenticated: false,
      isInitializing: true,
      isReadOnly: false,
      subscription: null,
      onlineStore: null,
      forcePasswordChange: false,
      isTenantResolved: false,
      orphanReason: null,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setTenant: (tenant) =>
        set(tenant ? { tenant, isTenantResolved: true, orphanReason: null } : { tenant }),
      setRole: (role) => set({ role }),
      setErpCredentials: (erpApiKey, erpApiSecret) => set({ erpApiKey, erpApiSecret }),
      setInitializing: (isInitializing) => set({ isInitializing }),
      setIsReadOnly: (isReadOnly) => set({ isReadOnly }),
      setSubscription: (subscription) => set({ subscription }),
      setOnlineStore: (onlineStore) => set({ onlineStore }),
      setForcePasswordChange: (forcePasswordChange) => set({ forcePasswordChange }),
      setTenantResolved: (isTenantResolved) => set({ isTenantResolved }),
      setOrphanReason: (orphanReason) => set({ orphanReason }),
      clearSession: () =>
        set({
          user: null,
          tenant: null,
          role: null,
          erpApiKey: null,
          erpApiSecret: null,
          isAuthenticated: false,
          isReadOnly: false,
          subscription: null,
          onlineStore: null,
          forcePasswordChange: false,
          isTenantResolved: false,
          orphanReason: null,
        }),
    }),
    {
      name: 'palstro-auth',
      partialize: (state) => ({
        user: state.user,
        tenant: state.tenant,
        role: state.role,
        erpApiKey: state.erpApiKey,
        erpApiSecret: state.erpApiSecret,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
